import { Appointment, AppointmentStatus, deriveWorkPhase } from "@myapp/contract";
import { ISelectOption } from "../../../shared/types/ISelectOption";

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  PLANNED: "Geplant",
  FOLLOW_UP: "Folgetermin geplant",
  COMPLETED: "Abgeschlossen",
  COMPLETED_WITH_FOLLOW_UP: "Folgetermin erforderlich",
  EXPIRED: "Abgelaufen",
};

const IN_PROGRESS_LABEL = "In Bearbeitung";
const IN_PROGRESS_COLOR = "#ED9E42";

export const isAppointmentInProgress = (
  a: Pick<Appointment, 'status' | 'schedule'>,
): boolean => (a.status === 'PLANNED'|| a.status == 'FOLLOW_UP') && !!a.schedule.actualWorkStartsAt && !a.schedule.actualEndsAt;

/**
 * Both terminal states. "Folgetermin erforderlich" is closed too — the visit happened and its
 * actuals are written; it only differs in owing a successor Termin.
 */
export const isAppointmentClosed = (status: AppointmentStatus): boolean =>
  status === "COMPLETED" || status === "COMPLETED_WITH_FOLLOW_UP";

export const getAppointmentStatusLabel = (
  status: AppointmentStatus,
  appointment?: Pick<Appointment, 'status' | 'schedule'>,
): string => {
  if (appointment) {
    const phase = deriveWorkPhase(appointment.schedule);
    if (phase === 'ANFAHRT') return 'Anfahrt läuft';
    if (phase === 'ABFAHRT') return 'Abfahrt läuft';
    if (isAppointmentInProgress(appointment)) return IN_PROGRESS_LABEL;
  }
  return STATUS_LABELS[status];
};

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  PLANNED: "#2EB5D9",
  FOLLOW_UP: "#2EB5D9",
  COMPLETED: "#3BC67D",
  COMPLETED_WITH_FOLLOW_UP: "#ED9E42",
  EXPIRED: "#9CA3AF",
};

export const getAppointmentStatusColor = (
  status: AppointmentStatus,
  appointment?: Pick<Appointment, 'status' | 'schedule'>,
): string => {
  if (appointment) {
    // Mirror `getAppointmentStatusLabel`: all three running phases read as "in progress", not only
    // ARBEIT. Without this, "Anfahrt läuft" painted the same blue as "Geplant" and "Abfahrt läuft"
    // on an already-completed appointment painted green — an active session did not look active.
    const phase = deriveWorkPhase(appointment.schedule);
    if (phase === 'ANFAHRT' || phase === 'ABFAHRT' || isAppointmentInProgress(appointment)) {
      return IN_PROGRESS_COLOR;
    }
  }
  return STATUS_COLORS[status];
};

/**
 * Every status, for the select in the edit form. All five are offered — including EXPIRED, which
 * the nightly cron normally owns — because the form exists to correct a status the system got
 * wrong, and a state a manager cannot leave is the bug it is there to fix.
 *
 * "In Bearbeitung" is deliberately absent: it is derived from the schedule's actual timestamps,
 * not a stored status, so there is nothing here to select.
 */
export const appointmentStatusOptions: ISelectOption[] = (
  Object.keys(STATUS_LABELS) as AppointmentStatus[]
).map((status) => ({ value: status, label: STATUS_LABELS[status] }));
